import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { CalculatorFill, InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import AcreageCalculator from "../Slides/AcreageCalculator.jsx";
import { FormContext } from "../context/FormProvider.jsx";
import { handleKeyDown, updateNumber } from "../main-helper.js";
import * as Messages from "../messages/messages.js";
import "../styles/form-container.css";
import "./styles/new-text.css";

const NewText = ({
  idProp,
  label,
  val,
  state,
  setState,
  req,
  debouncedSave,
  setIsLoading,
  autoData,
  type,
  disabledInput,
  invalidData,
}) => {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHelper, setOpenHelper] = useState(false);
  const [openCalculator, setOpenCalculator] = useState({
    totAcr: false,
    distAcr: false,
  });
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [adjustMargin, setAdjustMargin] = useState(false);
  const { requestStatus, handleInputFocus, handleInputDisable } =
    useContext(FormContext);
  const { capitalizeNameAfterSpace, userId, formatTaxId, formatFaxId } =
    useContext(DashboardContext);

  useEffect(() => {
    if (req && openHelper) {
      setAdjustMargin(true);
    } else {
      setAdjustMargin(false);
    }
  }, [req, openHelper]);

  // Function to handle text input change
  const handleTextChange = (e) => {
    let newState;

    if (type === "taxId") {
      newState = { ...state, [idProp]: formatTaxId(e.target.value) };
      setState(newState);
      if (debouncedSave && setIsLoading && requestStatus !== "A") {
        setIsLoading(() => true);
        debouncedSave.mutate(newState);
      }
      return;
    }

    if (type === "fax") {
      newState = { ...state, [idProp]: formatFaxId(e.target.value) };
      setState(newState);
      if (debouncedSave && setIsLoading && requestStatus !== "A") {
        setIsLoading(() => true);
        debouncedSave.mutate(newState);
      }
      return;
    }

    if (idProp === "jobNumber") {
      newState = { ...state, [idProp]: e.target.value };
      setState(newState);
      if (debouncedSave && setIsLoading && requestStatus !== "A") {
        setIsLoading(() => true);
        debouncedSave.mutate(newState);
      }
      return;
    }

    if (
      idProp === "totAcr" ||
      idProp === "distAcr" ||
      idProp === "totalBldLots" ||
      idProp === "phases"
    ) {
      let value = e.target.value;
      updateNumber(idProp, value, state, setState, debouncedSave, setIsLoading);

      return;
    }

    if (idProp === "csw") {
      // Remove non-numbers
      let value = e.target.value.replace(/[^0-9]/g, "");
      newState = { ...state, [idProp]: value };
      setState(newState);
      if (debouncedSave && setIsLoading && requestStatus !== "A") {
        setIsLoading(() => true);
        debouncedSave.mutate(newState);
      }
      return;
    }
    let value;

    if (idProp === "crossRoad1" || idProp === "crossRoad2") {
      // Capitalize the first letter of the value
      value = e.target.value
        ? e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1)
        : "";
      newState = { ...state, [idProp]: value };
      setState(newState);
      if (debouncedSave && setIsLoading && requestStatus !== "A") {
        setIsLoading(() => true);
        debouncedSave.mutate(newState);
      }
      return;
    }

    if (type === "name") {
      value = capitalizeNameAfterSpace(e.target.value);
    } else {
      value = e.target.value;
    }
    let formattedValue = value.replace(/[^a-zA-Z0-9- ,'.& # @]/g, "");
    newState = { ...state, [idProp]: formattedValue };
    setState(newState);

    if (
      showPlaceholder &&
      formattedValue !== "" &&
      !placeholder.toLowerCase().startsWith(formattedValue.toLowerCase())
    ) {
      if (autoData && autoData.length > 0) {
        let match = autoData.find((val) => {
          if (!val) return false;
          return val.toLowerCase().startsWith(formattedValue.toLowerCase());
        });

        if (match) {
          match = formattedValue + match.slice(formattedValue.length);
        }

        if (match && formattedValue !== "") {
          setPlaceholder(match);
          setShowPlaceholder(true);
        } else {
          setPlaceholder("");
          setShowPlaceholder(false);
        }
      }
    } else if (
      value !== "" &&
      placeholder.toLowerCase().startsWith(value.toLowerCase())
    ) {
      let existingMatch = value + placeholder.slice(value.length);
      setPlaceholder(existingMatch);
      setShowPlaceholder(true);
    } else if (value === "") {
      setPlaceholder("");
      setShowPlaceholder(false);
    }

    if (debouncedSave && setIsLoading) {
      setIsLoading(true);
      debouncedSave.mutate(newState);
    }
  };
  return (
    <div
      className={`text-info-container ${type}-info-container`}
      id={
        idProp === "totAcr" || idProp === "distAcr"
          ? "acreage-info-container"
          : ""
      }
    >
      {(idProp === "totAcr" || idProp === "distAcr") &&
        !handleInputDisable(idProp, state, disabledInput, userId) && (
          <Tooltip
            id="calculator-tooltip"
            classes={{
              tooltip: "calculator-tooltip",
              popper: "calculator-popper",
              arrow: "calculator-arrow",
              tooltipPlacementTop: "calculator-top",
            }}
            title={`Open calculator to convert square feet (ft²) to acres`}
            placement="top"
            arrow
          >
            <IconButton
              id="calculator-icon-btn"
              onClick={() => {
                setOpenCalculator({
                  ...openCalculator,
                  [idProp]: !openCalculator[idProp],
                });
              }}
              aria-label="open acreage calculator"
            >
              <CalculatorFill className="calculator-icon" />
            </IconButton>
          </Tooltip>
        )}
      <FormControl
        variant="outlined"
        id="text-form"
        classes={{ root: "form-text-root" }}
        className={`${type}-form-control ${
          requestStatus === "RV" &&
          disabledInput === false &&
          invalidData &&
          (val === invalidData["initialValue"] || val === "")
            ? "revise-input"
            : requestStatus === "RV" &&
                disabledInput === false &&
                invalidData &&
                val !== invalidData["initialValue"] &&
                val !== ""
              ? "revised-input"
              : handleInputDisable(idProp, state, disabledInput, userId) ===
                  true
                ? "disable-text"
                : req
                  ? "required"
                  : ""
        }`}
      >
        <InputLabel
          id="text-field-label"
          htmlFor="text-input"
          classes={{ shrink: "text-field-label-shrink" }}
          className={`${idProp}-text-field-label`}
        >
          {`${label}`}
        </InputLabel>
        <OutlinedInput
          type="text"
          id="text-field"
          className={`${idProp}-input`}
          ref={textRef}
          label={`${label}`}
          value={val}
          slotProps={{
            input: { maxLength: idProp === "csw" ? 5 : 75 },
          }}
          onChange={handleTextChange}
          onFocus={() => handleInputFocus(textRef, setOpenHelper)}
          onSelect={() => {
            if (state[`${idProp}`] === "") {
              setOpen(true);
            } else {
              setOpen(false);
            }
            setOpenHelper(true);
            setShowPlaceholder(true);
          }}
          onBlur={() => {
            setOpen(false);
            setShowPlaceholder(false);
            setPlaceholder("");
            setOpenHelper(false);
            if (idProp === "totAcr") {
              setOpenCalculator({
                ...openCalculator,
                totAcr: false,
              });
            } else if (idProp === "distAcr") {
              setOpenCalculator({
                ...openCalculator,
                distAcr: false,
              });
            }
          }}
          onKeyDown={(e) =>
            handleKeyDown(
              e,
              idProp,
              state,
              setState,
              autoData,
              placeholder,
              debouncedSave,
              setIsLoading,
              setPlaceholder,
              setShowPlaceholder,
              setOpen,
              setOpenHelper,
            )
          }
          disabled={
            handleInputDisable(idProp, state, disabledInput, userId) === true
              ? true
              : false
          }
          autoComplete="off"
          autoCapitalize="words"
          classes={{
            root: "text-field-root",
            notchedOutline: "text-notchedOutline",
            input: "text-field-input",
          }}
          endAdornment={
            <Tooltip
              id="clear-btn-tooltip"
              classes={{
                tooltip: "tooltip-clear-btn",
                popper: "popper-clear-btn",
                tooltipPlacementTop: "tooltip-top",
              }}
              title={val === "" ? "" : "Clear Field"}
              placement="top"
            >
              <InputAdornment
                className="text-adornment"
                id="text-adornment"
                position="end"
                style={
                  val === "" ||
                  handleInputDisable(idProp, state, disabledInput, userId) ===
                    true
                    ? { visibility: "hidden" }
                    : { visibility: "visible" }
                }
              >
                <IconButton
                  style={
                    val === ""
                      ? {
                          pointerEvents: "none",
                          cursor: "default",
                        }
                      : {
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }
                  }
                  aria-label="clear input field"
                  onClick={() => {
                    if (debouncedSave) {
                      let newState = {
                        ...state,
                        [idProp]: "",
                      };
                      setState(newState);
                      setIsLoading(true);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                      debouncedSave.mutate(newState);
                    } else {
                      let newState = {
                        ...state,
                        [idProp]: "",
                      };
                      setState(newState);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  className="clear-icon-button"
                  id="clear-icon-button"
                  disabled={val === "" ? true : false}
                >
                  <CloseIcon id="clear-input-icon" className="close-icon" />
                </IconButton>
              </InputAdornment>
            </Tooltip>
          }
        />
      </FormControl>
      <input
        key={`${idProp}-placeholder`}
        className={`${
          req ? "predictive-placeholder shift" : "predictive-placeholder"
        }`}
        type="text"
        autoComplete="off"
        id={`${type}-placeholder`}
        value={placeholder}
        disabled={true}
      />
      <Tooltip
        id="tf-tooltip"
        classes={{
          tooltip: "text-field-tooltip",
          popper: "text-field-popper",
          arrow: "text-field-arrow",
          tooltipPlacementLeft: "text-field-right",
        }}
        title={Messages[`${idProp}Message`]}
        placement={"top"}
        arrow
        open={open}
        sx={{
          backgroundColor: "rgb(0, 0, 0, 0.8) !important",
        }}
      >
        <InfoCircle
          className="text-info-circ"
          id={`info-${idProp}`}
          color="white"
          size={30}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        />
      </Tooltip>
      {idProp === "totAcr" && openCalculator["totAcr"] && (
        <AcreageCalculator
          idProp={"totAcr"}
          state={state}
          setState={setState}
        />
      )}
      {idProp === "distAcr" && openCalculator["distAcr"] && (
        <AcreageCalculator
          idProp={"distAcr"}
          state={state}
          setState={setState}
        />
      )}
    </div>
  );
};

export default NewText;
